import React, { useEffect, useRef, useState } from 'react';
import { Input, Button, Alert } from '../../components/ui';
import { readMaintenanceWorkbook } from './workbook';

export default function WorkbookUpload({ onLoaded, onClear, disabled = false }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);
  useEffect(() => () => { request.current += 1; }, []);
  const upload = async (selectedFile) => {
    const id = ++request.current;
    setBusy(true); setError(''); onClear?.();
    try {
      const data = await readMaintenanceWorkbook(selectedFile);
      if (id === request.current) onLoaded({ ...data, filename: selectedFile.name });
    } catch (e) { if (id === request.current) setError(e.message || 'Workbook import failed.'); }
    finally { if (id === request.current) setBusy(false); }
  };
  return <div className="space-y-4">
    <p className="text-sm text-muted-fg">Upload one .xlsx workbook containing CM and PPM sheets. The reporting month is detected automatically from work-order dates in both sheets. Sheets are identified by their column headers. Selecting a file imports it automatically in your browser.</p>
    <div>
      <Input label="CM and PPM workbook (.xlsx)" type="file" accept=".xlsx" disabled={busy || disabled} onChange={(e) => {
        const selectedFile = e.target.files?.[0];
        e.target.value = '';
        if (selectedFile) { setFile(selectedFile); upload(selectedFile); }
      }} />
    </div>
    {file && <p className="text-sm text-muted-fg">{file.name}</p>}
    {busy && <p role="status">Reading workbook and filling report data…</p>}
    {error && file && <Button disabled={busy || disabled} onClick={() => upload(file)}>Retry import</Button>}
    {error && <Alert variant="error" title="Workbook could not be imported">{error}</Alert>}
  </div>;
}

export function ImportSummary({ data }) {
  if (!data) return null;
  const skipped = data.diagnostics.filter((d) => d.skipped).length;
  const warnings = data.diagnostics.length - skipped;
  return <div className="space-y-2 text-sm" aria-live="polite">
    <p className="font-medium">{data.filename}: {data.cm.length} CM and {data.pm.length} PPM records imported for {data.month} (detected from workbook dates).</p>
    <p>{skipped} records skipped / excluded. {warnings} records with invalid dates retained in totals.</p>
    {data.diagnostics.length > 0 && <details className="rounded-md border border-border p-3"><summary className="cursor-pointer">Review record warnings ({data.diagnostics.length})</summary>
      <ul className="mt-2 max-h-64 overflow-auto space-y-1">{data.diagnostics.map((d, i) => <li key={i}>{d.sheet}, row {d.row}: {d.reason}</li>)}</ul>
    </details>}
    <p className="text-muted-fg">Blank locations remain a distinct value, matching the notebook. Blank or unknown maintenance frequencies are shown explicitly.</p>
  </div>;
}
